import { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { EmptyCard, ErrorCard, LoadingBlock } from "./AdminBits";
import { SortHeader } from "./analysis/AnalysisBits";
import { orderingDirection } from "../../lib/jamoraAnalysis";

// Pill-shaped filter tabs ([value, label]); the first value is usually "" = « all ».
export function FilterTabs({ label, tabs, value, onChange, testid }) {
  return (
    <div className="flex gap-1 flex-wrap" role="tablist" aria-label={label}>
      {tabs.map(([id, text]) => (
        <button key={id || "all"} type="button" role="tab" aria-selected={value === id} onClick={() => onChange(id)} data-testid={`${testid}-${id || "all"}`}
          className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap ${value === id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>{text}</button>
      ))}
    </div>
  );
}

// A search field that reports its text 300 ms after the last keystroke.
export function SearchBox({ value, onChange, placeholder, label, testid }) {
  const [text, setText] = useState(value || "");
  useEffect(() => { setText(value || ""); }, [value]);
  useEffect(() => {
    if (text.trim() === (value || "")) return undefined;
    const timer = setTimeout(() => onChange(text.trim()), 300);
    return () => clearTimeout(timer);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
      <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} className="pl-9 h-9" aria-label={label || placeholder} data-testid={testid} />
    </div>
  );
}

// The table on a computer, one card per row on a phone (same rows, same cells). `columns`: [{ id, label, cell(row), hide?: "lg" | "xl", primary? }].
export function AdminTable({ columns, rows, rowKey, testid, onRowClick, ordering = "", onOrder }) {
  const hideClass = { lg: "hidden lg:table-cell", xl: "hidden xl:table-cell" };
  const primary = columns.find((c) => c.primary) || columns[0];
  const others = columns.filter((c) => c !== primary);
  const clickable = onRowClick ? { role: "button", tabIndex: 0 } : {};
  const onKey = (row) => (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRowClick(row); } };
  return (
    <>
      <Card className="hidden md:block overflow-hidden" data-testid={testid}>
        <Table>
          <TableHeader><TableRow>{columns.map((c) => <TableHead key={c.id} className={hideClass[c.hide] || ""} aria-sort={c.sort && onOrder ? orderingDirection(ordering, c.sort) : undefined}>{c.sort && onOrder ? <SortHeader column={c} ordering={ordering} onOrder={onOrder} /> : c.label}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={rowKey(row)} data-testid={`${testid}-row-${rowKey(row)}`} className={onRowClick ? "cursor-pointer" : ""}
                onClick={onRowClick ? () => onRowClick(row) : undefined} onKeyDown={onRowClick ? onKey(row) : undefined} {...clickable}>
                {columns.map((c) => <TableCell key={c.id} className={`${hideClass[c.hide] || ""} text-sm`}>{c.cell(row)}</TableCell>)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Card className="md:hidden divide-y divide-slate-100" data-testid={`${testid}-cards`}>
        {rows.map((row) => (
          <div key={rowKey(row)} className="px-4 py-3 space-y-1.5" data-testid={`${testid}-card-${rowKey(row)}`} onClick={onRowClick ? () => onRowClick(row) : undefined}
            onKeyDown={onRowClick ? onKey(row) : undefined} {...clickable}>
            <div className="font-medium text-slate-900 break-words">{primary.cell(row)}</div>
            <dl className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-sm">
              {others.map((c) => (
                <div key={c.id} className="contents"><dt className="text-xs text-slate-500 pt-0.5">{c.label}</dt><dd className="text-slate-800 min-w-0 break-words">{c.cell(row)}</dd></div>
              ))}
            </dl>
          </div>
        ))}
      </Card>
    </>
  );
}

// loading / error / empty around a list from `useAdminList` (or the same shape).
export function ListFrame({ list, empty, children, rows = 4 }) {
  if (list.phase === "loading" || list.phase === "idle") return <LoadingBlock rows={rows} />;
  if (list.phase === "error") return <ErrorCard message={list.error} onRetry={list.reload} />;
  if (!list.items.length) return <EmptyCard icon={empty.icon} title={empty.title} hint={empty.hint} testid={empty.testid || "admin-empty"}>{empty.children}</EmptyCard>;
  return children;
}

export function MoreFooter({ list, noun, testid }) {
  if (!list.items.length) return null;
  return (
    <div className="flex items-center justify-between gap-3">
      <p className="text-xs text-slate-500" data-testid={`${testid}-count`}>{list.items.length} {noun}{list.cursor ? " (il y en a d'autres)" : ""}</p>
      {list.cursor && (
        <Button variant="outline" size="sm" onClick={list.more} disabled={list.phase === "more"} data-testid={`${testid}-more`}>
          {list.phase === "more" && <Loader2 size={14} className="mr-1.5 animate-spin" />}Voir plus
        </Button>
      )}
    </div>
  );
}
